import React from 'react';

import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';

import styled from '@emotion/styled';
import { Field, useField } from 'formik';

import { SHIPPING_AVAILABILITY } from '../../../components/Dialogs/CollabsFilters/options';
import { HtmlTooltip } from '../../../components/HtmlTooltip/HtmlTooltip';
import { BlackIOSSwitch } from '../../../components/IOSSwitch';
import { ShippingRegion } from '../../../graphql/API';
import Icons from '../../../themes/icons';
import { StepOneFormValue, StepThreeFormValue, StepTwoFormValue } from './schema';

const shippingRegions = Object.values(ShippingRegion);

type Values = StepOneFormValue & StepTwoFormValue & StepThreeFormValue;

type Props = {
  label?: string;
  name?: string;
  defaultValue?: string;
  values: Values;
};

const ShippingAvailability: React.FC<Props> = ({
  label = 'Shipping availability',
  name = 'shippingRegions',
  defaultValue = 'global',
  values,
}) => {
  const [, meta, helper] = useField(name);

  const handleOnChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, region: ShippingRegion) => {
      const checked = e.target.checked;
      const currentRegions = values.shippingRegions || [];

      if (checked) {
        helper.setValue([...currentRegions, region]);
      } else {
        helper.setValue(currentRegions.filter((r) => r !== region));
      }
    },
    [helper, values.shippingRegions],
  );

  return (
    <Box>
      <Box display={'flex'} alignItems={'center'} gap={'8px'} mb={'15px'}>
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        <HtmlTooltip
          arrow
          placement='top'
          title={
            <React.Fragment>
              <Typography fontSize={'15px'} p={'12px'}>
                The region where you agree to ship your product
              </Typography>
            </React.Fragment>
          }
        >
          <Icons.InfoIcon />
        </HtmlTooltip>
      </Box>

      <FormControlLabel
        sx={{ m: 0, gap: '6px' }}
        control={
          <BlackIOSSwitch
            checked={
              (values.shippingRegions?.length || 0) > 0
                ? values.shippingRegions?.includes(ShippingRegion.global)
                : false
            }
            onChange={(e) => {
              const checked = e.target.checked;
              if (checked) {
                helper.setValue([ShippingRegion.global]);
              } else {
                helper.setValue([ShippingRegion.us]);
              }
            }}
          />
        }
        label={SHIPPING_AVAILABILITY['global']}
        labelPlacement='top'
      />

      {!values.shippingRegions?.includes(ShippingRegion.global) && (
        <FormGroup sx={{ my: 1 }}>
          {shippingRegions
            .filter((key) => key !== ShippingRegion.global)
            .map((value) => (
              <FormControlLabel
                key={value}
                control={
                  <Field
                    type='checkbox'
                    name={name} // Same name for all checkboxes
                    value={value} // Set the value of each checkbox
                    as={Checkbox}
                    checked={values?.shippingRegions?.includes(value)} // Check if selected
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange(e, value)}
                  />
                }
                label={SHIPPING_AVAILABILITY[value]}
                sx={{ '& .MuiSvgIcon-root': { fontSize: 26 } }}
              />
            ))}
        </FormGroup>
      )}
      {meta.error && meta.touched && <FormError>{meta.error}</FormError>}
    </Box>
  );
};

export default ShippingAvailability;

const FormError = styled.span`
  margin: 1rem 0;
  color: #ff3d3d;
`;
